import React, {Fragment, useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Badge, Breadcrumb, Container, Dropdown, Nav, Navbar, NavDropdown, NavItem} from 'react-bootstrap';
import {config} from "../../Config";
import {CookieUtils} from "../../Utils";
import {useChangePage} from "../../Hooks/NavigationHooks";
import {Glyphicon} from "../Common/GlyphIcon";
import {StreamBankContext} from "../../App/StreamBankContext";
import {StreamBankLink} from "../Common/StreamBankLink";
import {
    useAnyPermission,
    usePermission,
    usePublic,
} from "../../Hooks/PermissionHooks";
import {
    useScroll
} from "../../Hooks/ScrollHooks";
import {
    useChangeContextFromString,
} from "../../Hooks/RouteHooks";

import {SearchBar} from "./SearchBar";
import {SBATermsOfServiceModal} from "../Modals/SBATermsOfServiceModal";
import {USER_SUBDOMAIN_DATA_SHARE_COOKIE_NAME} from '../../Constants';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import * as toolbarItemComponents from "./Toolbar/ToolbarItems";
import {useUserProfileImage} from "../../Hooks/useUserProfileImage";
import {service as UserService} from "../../Services/UserService";
import {useFetchServiceModel} from "../../Hooks/ServiceHooks";

export const Header = props => {
    const permission = usePermission();
    const anyPermission = useAnyPermission();
    const isPublic = usePublic();
    const {scrollDirection} = useScroll();
    const changeContextFromString = useChangeContextFromString();
    const changePage = useChangePage();

    const {
        context: appContext,
        breadcrumbs,
        toolbars
    } = useSelector(state => state.contextState);
    const {notifications} = useSelector(state => state.notificationState);

    const appUrl = window.location.pathname;
    const user = useSelector(state => state.userState.user);
    const userModel = useFetchServiceModel(UserService, user?.userId);
    const userDisplayName = userModel ? UserService.GetTitle(userModel) : null;
    const currentUserProfileImageUrl = useUserProfileImage(user);

    const [lastNonAdminUrl, setLastNonAdminUrl] = useState('/programs');
    const [showAlt, setShowAlt] = useState(true);
    const [showAccountMenu, setShowAccountMenu] = useState(true);
    const [showContextBar, setShowContextBar] = useState(true);
    const [showToolBar, setShowToolBar] = useState(true);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

    useEffect(() => {
        scrollToRevealDeepestCrumb();
    }, [appContext, breadcrumbs]);

    useEffect(() => {
        if(user == null) {
            CookieUtils.ClearCookie(USER_SUBDOMAIN_DATA_SHARE_COOKIE_NAME, true);
        } else {
            CookieUtils.WriteCookie(USER_SUBDOMAIN_DATA_SHARE_COOKIE_NAME, currentUserProfileImageUrl, user.activeUntil, true);
        }
    }, [user?.activeUntil, currentUserProfileImageUrl]);

    useEffect(() => {
        const shouldHideNavigation = () => {
            return appUrl?.toLowerCase?.()?.startsWith("/login") || appUrl?.toLowerCase?.()?.startsWith("/forgotpassword");
        };

        const shouldDisplayAlt = (getCurrentAltBreadcrumb() != "");

        setShowAccountMenu(!shouldHideNavigation());
        setShowContextBar(!shouldHideNavigation());
        setShowToolBar(!shouldHideNavigation());

        if (!shouldDisplayAlt && !showAlt) {
            setLastNonAdminUrl(appUrl);
        }

        setShowAlt(shouldDisplayAlt);
    }, [appUrl]);

    const handleDropdownLinkClick = (event) => {
        setIsAccountMenuOpen(false);
    }

    const scrollToRevealDeepestCrumb = () => {
        // Scroll to end for mobile
        const breadcrumbElement = document.querySelector('.streambank-breadcrumbs .breadcrumb');
        if(breadcrumbElement) {
            breadcrumbElement.scrollLeft = document.body.scrollWidth;
        }
    };

    const goToFeedbackPage = () => {
        let url = 'https://app.smartsheet.com/b/form/86362a0ba7a34504a5e99da450463ead';
        let win = window.open(url, '_blank');
        win.focus();
    };

    const getCurrentAltBreadcrumb = () => {
        switch(appUrl?.toLowerCase?.()) {
            case "/users/" + (user?.userId || ""):
            case "/users/subscriptions":
                return "Account";
            case "/users":
            case "/organizations":
            case "/surveys":
            case "/conservationactiontypes":
                return "Permissions";
            case "/specieslists":
            case "/allspecies":
            case "/speciesrequests":
                return "Plant Species";
            case "/query":
            case '/reportbuilder':
                return "Reports";
        }

        if(appUrl?.toLowerCase?.()?.startsWith("/users/")) {
            return "Permissions";
        } else if(appUrl?.toLowerCase?.()?.startsWith("/organizations/")) {
            return "Permissions";
        } else if(appUrl?.toLowerCase?.()?.startsWith("/surveys/")) {
            return "Permissions";
        }  else if(appUrl?.toLowerCase?.()?.startsWith("/conservationactiontypes/")) {
            return "Permissions";
        } else if(appUrl?.toLowerCase?.()?.startsWith("/specieslists/")) {
            return "Plant Species";
        } else if(appUrl?.toLowerCase?.()?.startsWith("/allspecies/")) {
            return "Plant Species";
        } else if(appUrl?.toLowerCase?.()?.startsWith("/query/")) {
            return "Reports";
        } else if(appUrl?.toLowerCase?.()?.startsWith("/reportbuilder/")) {
            return "Reports";
        }

        return "";
    };

    const getAltBreadcrumb = (title, url) => {
        let isActive = title == getCurrentAltBreadcrumb();

        return <StreamBankLink
            className={"toolbar-item btn" + (isActive ? " active": "")}
            Href={url}
        >
            {title}
        </StreamBankLink>;
    };

    const getAltBreadcrumbs = () => {
        const hasPermissionsTab = anyPermission(['update_organization', 'create_user', 'can_update_survey', 'update_conservation_action_type']);
        const hasSpeciesTab = permission('can_view_species_list');
        // TODO can everyone see report builder?

        return <Fragment>
            {getAltBreadcrumb("Account", "/users/" + user?.userId)}
            {hasPermissionsTab ? getAltBreadcrumb("Permissions", "/users"): null}
            {hasSpeciesTab ? getAltBreadcrumb("Plant Species", "/allSpecies"): null}
            {getAltBreadcrumb("Reports", "/reportBuilder")}
        </Fragment>;
    };

    const getAltTab = (title, url, notification = null) => {
        let isActive = appUrl.startsWith(url);

        return <StreamBankLink
            className={"toolbar-item btn" + (isActive ? " active": "")}
            Href={url}
        >
            {title}
            {notification > 0 && <Badge variant="danger" className="ml-1" pill>{notification}</Badge>}
        </StreamBankLink>;
    };

    const getAltTabs = () => {
        const group = getCurrentAltBreadcrumb();

        switch(group) {
            case "Account":
                return <Fragment>
                    {getAltTab("Profile", "/users/" + user?.userId)}
                    {getAltTab("Subscriptions", "/users/subscriptions")}
                </Fragment>;
            case "Permissions":
                return <Fragment>
                    {permission('create_user') ? getAltTab("Users", "/users"): null}
                    {permission('update_organization') ? getAltTab("Organizations", "/organizations"): null}
                    {permission('can_update_survey') ? getAltTab("Surveys", "/surveys"): null}
                    {permission('update_conservation_action_type') ? getAltTab("Conservation Action Types", "/conservationActionTypes"): null}
                </Fragment>;
            case "Plant Species":
                return <Fragment>
                    {permission('can_view_species_list') ? getAltTab("Global List", "/allSpecies"): null}
                    {permission('can_view_species_list') ? getAltTab("Custom Lists", "/speciesLists"): null}
                    {permission('can_view_species_list') ? getAltTab("Species Requests", "/speciesRequests", notifications?.speciesRequestCount): null}
                </Fragment>;
            case "Reports":
                return <Fragment>
                    {permission('can_view_query_tool') ? getAltTab("Report Builder", "/reportBuilder"): null}
                    {permission('can_view_query_tool') ? getAltTab("Query", "/query"): null}
                </Fragment>;
        }

        return null;
    };

    const getToolbar = () => {
        return toolbars.map(toolbar => {
            const DynamicToolbarItem = toolbarItemComponents[toolbar.toolbarItemComponent ?? 'ToolbarItem'];
            return <DynamicToolbarItem {...{...toolbar, code: toolbar.key}} />;
        });
    };

    const scrollClass = scrollDirection ? 'mobile-scroll-' + scrollDirection : null;
    let envClass = '';
    let envTitle;

    if (config.getEnvironment() !== 'prod') {
        envClass = 'streambank-navbar-' + config.getEnvironment();
        envTitle = config.getEnvironment() + ' environment';
    }

    return <Fragment>
        <Navbar className={`streambank-navbar ${scrollClass} ${envClass}`}>
            <Container>
                <SBATermsOfServiceModal />
                <Navbar.Brand>
                    <StreamBankLink Href="/programs">
                        <img alt="streambank" src="/img/sba-logo-white-no-trademark.svg" />
                    </StreamBankLink>
                </Navbar.Brand>
                {
                    envTitle && <div className="streambank-env-title">{envTitle}</div>
                }
                <Nav>
                    <a className="sb-library-icon" title="StreamBank Library" target="_blank" href={config.getUrl('library') + "/books"}>
                        <FontAwesomeIcon icon={['fas', 'books']} />
                    </a>
                    {
                        showAccountMenu ? (
                        user !== null && !isPublic ?
                                <NavDropdown
                                    alignRight
                                    active={isAccountMenuOpen}
                                    title={
                                        <Fragment>
                                            <div className="sb-account-icon">
                                                <img
                                                    src={currentUserProfileImageUrl}
                                                    alt=""
                                                />
                                            </div>
                                            <span className="sb-account-title">
                                                {userDisplayName}
                                            </span>
                                        </Fragment>
                                    }
                                    id="sb-account-dropdown"
                                >
                                    {
                                        anyPermission(['update_organization', 'create_user', 'can_update_survey', 'update_conservation_action_type']) ?
                                            <Dropdown.Header className="sb-header-menu-title">
                                                Administration
                                            </Dropdown.Header>:
                                            null
                                    }
                                    {
                                        permission('update_organization') ?
                                            <Dropdown.Item href={'/organizations'}>
                                                Organizations
                                            </Dropdown.Item> :
                                            null
                                    }
                                    {
                                        permission('create_user') ?
                                            <Dropdown.Item href={'/users'}>
                                                Users
                                            </Dropdown.Item> :
                                            null
                                    }
                                    {
                                        permission('can_update_survey') ?
                                            <Dropdown.Item href={'/surveys'}>
                                                Surveys
                                            </Dropdown.Item> :
                                            null
                                    }
                                    {
                                        permission('update_conservation_action_type') ?
                                            <Dropdown.Item href={'/conservationActionTypes'}>
                                                    Conservation Action Types
                                            </Dropdown.Item> :
                                            null
                                    }
                                    {
                                        anyPermission(['update_organization', 'create_user', 'can_update_survey', 'update_conservation_action_type']) ?
                                            <Dropdown.Divider />:
                                            null
                                    }
                                    {
                                        anyPermission(['can_view_species_list', 'can_view_query_tool']) ?
                                            <Dropdown.Header className="sb-header-menu-title">
                                                Data Tools
                                            </Dropdown.Header>:
                                            null
                                    }
                                    {
                                        permission('can_view_species_list') ?
                                            <Dropdown.Item href={'/allSpecies'}>
                                                <div className="d-flex justify-content-between">
                                                    <span>Plant Species</span>
                                                    {
                                                        notifications?.speciesRequestCount > 0 &&
                                                        <div>
                                                            <Badge variant="danger" pill>{notifications.speciesRequestCount}</Badge>
                                                        </div>
                                                    }
                                                </div>
                                            </Dropdown.Item> :
                                            null
                                    }
                                    {
                                            <Dropdown.Item href={'/reportBuilder'}>
                                                Report Builder
                                            </Dropdown.Item>
                                    }
                                    {
                                        permission('can_view_query_tool') ?
                                            <Dropdown.Item href={'/query'}>
                                                Query
                                            </Dropdown.Item> :
                                            null
                                    }
                                    {
                                        anyPermission(['can_view_species_list', 'can_view_query_tool']) ?
                                            <Dropdown.Divider />:
                                            null
                                    }

                                    <Dropdown.Header className="sb-header-menu-title">
                                        Account
                                    </Dropdown.Header>
                                    <Dropdown.Item href={`/users/${user.userId}`}>
                                        Profile
                                    </Dropdown.Item>
                                    <Dropdown.Item href={'/users/subscriptions'}>
                                        Subscriptions
                                    </Dropdown.Item>
                                    <Dropdown.Item eventKey="2.3" onClick={() => goToFeedbackPage()}>User Feedback</Dropdown.Item>
                                    <Dropdown.Item eventKey="2.4" onClick={() => changePage("/logout")}>Logout</Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item className="help-link" eventKey="3.1" target="_blank" href={config.getUrl('library') + "/books"}>StreamBank Library</Dropdown.Item>
                                </NavDropdown>
                            :
                            <NavItem className="sb-login-button" onClick={() => changePage("/Login?redirectUrl=" + encodeURIComponent(window.location.pathname + window.location.search))}>
                                <a className="sb-login-title btn-link">Login</a>
                            </NavItem>
                        ) : null
                    }
                </Nav>
            </Container>
        </Navbar>
        {
            showContextBar ?
                <Navbar className={`streambank-breadcrumbs navbar-default ${scrollClass}`}>
                    <Container className="">

                            <Breadcrumb>
                                <Breadcrumb.Item
                                    linkAs={StreamBankLink}
                                    linkProps={{
                                        role: "button",
                                        Context: {}
                                    }}>
                                    Home
                                </Breadcrumb.Item>
                                {
                                    breadcrumbs.length ?
                                        breadcrumbs.map((x, i) => {
                                            
                                            // when the user clicks on the context
                                            // for the current tool, go to the default tool
                                            // for the context instead of not navigating anywhere
                                            let targetTool = null;
                                            if(appContext && (new StreamBankContext()).LoadData(x.context).Equals(appContext)) {
                                                targetTool = 'summary';
                                            }
                                            
                                            return <Breadcrumb.Item
                                                key={i}
                                                linkAs={StreamBankLink}
                                                linkProps={{
                                                    Context: x.context,
                                                    Tool: targetTool,
                                                    role: "button"
                                                }}>
                                                    <span className="breadcrumb-title">{x.title}</span>
                                                    <span className="breadcrumb-title-mobile">{x.shortTitle || x.title}</span>
                                            </Breadcrumb.Item>;
                                        }):
                                        null
                                }
                            </Breadcrumb>

                            {
                                !isPublic &&
                                <div className="autocomplete-column">
                                    <SearchBar
                                        OnUpdate={(x) => changeContextFromString(x.link)}
                                    />
                                </div>
                            }
                    </Container>
                    <Navbar className={"streambank-breadcrumbs-alt" + (showAlt ? " open": "")}>
                        <Container className="top7 bottom7">
                            {getAltBreadcrumbs()}
                            <div className="close-button ml-auto" onClick={_ => changePage(lastNonAdminUrl)}>
                                <Glyphicon glyph="times-circle"/>
                            </div>
                        </Container>
                    </Navbar>
                </Navbar>:
                null
        }
        {
            user !== null && showToolBar && toolbars.length ?
                <Navbar className={`streambank-tabs navbar-default ${scrollClass}`}>
                    <Container className="top10 bottom10">
                        {getToolbar()}
                    </Container>
                    <Navbar className={"streambank-tabs-alt" + (showAlt ? " open": "")}>
                        <Container className="top10 bottom10">
                            {getAltTabs()}
                        </Container>
                    </Navbar>
                </Navbar>:
                null
        }
    </Fragment>;
};
